import React, { useState, useEffect, useCallback } from "react";
import { Card, Button, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import axios from "axios";
import { getConfigWithToken } from "../config/config";
import { toast } from "react-toastify";
import CarePlanConcernsSection from "../outlets/sessionHistorySections/CarePlanConcernsSection";
import formatDate from "../utils/formatDate";
import { formatDateOnly, normalizeDateForInput, extractDateOnly, formatDateForBackend } from "../utils/formatDateOnly";

const CarePlanCard = ({ userId, initialData = null, onRefresh = null }) => {
  const user = useSelector((state) => state?.user);
  
  const [carePlanData, setCarePlanData] = useState(null);
  const [carePlanHistory, setCarePlanHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [carePlanEditMode, setCarePlanEditMode] = useState(null);
  const [originalCarePlanData, setOriginalCarePlanData] = useState(null);
  const [carePlanSaving, setCarePlanSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  
  // Form data state
  const [sessionFormData, setSessionFormData] = useState({
    clientGoal: "",
    objectives: "",
    interventions: "",
    sessionFrequency: "",
    otherSessionFrequencyText: "",
    reviewDate: "",
  });

  // Error states
  const [clientGoalError, setClientGoalError] = useState("");
  const [objectivesError, setObjectivesError] = useState("");
  const [interventionsError, setInterventionsError] = useState("");
  const [sessionFrequencyError, setSessionFrequencyError] = useState("");
  const [reviewDateError, setReviewDateError] = useState("");

  // Auto resize textarea function
  const autoResizeTextarea = (element, respectMinHeight = false) => {
    if (!element) return;
    if (respectMinHeight) {
      let minHeight = 72;
      if (element.style.minHeight) {
        const minHeightStr = element.style.minHeight;
        minHeight = parseInt(minHeightStr) || 72;
      }

      const hasValue = element.value && element.value.trim().length > 0;

      if (!hasValue) {
        element.style.height = `${minHeight}px`;
        return;
      }

      const calculateHeight = () => {
        element.style.height = "auto";
        void element.offsetHeight;
        const scrollHeight = element.scrollHeight;
        const newHeight = Math.max(minHeight, scrollHeight);
        element.style.height = `${newHeight}px`;
      };

      calculateHeight();
      requestAnimationFrame(() => {
        if (element && element.value) {
          calculateHeight();
        }
      });
    } else {
      element.style.height = "auto";
      element.style.height = `${element.scrollHeight}px`;
    }
  };

  // Update form data function
  const updateFormData = (fieldName, value) => {
    setSessionFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };


  // Process care plan data (extracted from fetchCarePlanData)
  const processCarePlanData = useCallback((data, versionToSelectAfterFetch = null) => {
    if (!data) return;

    // Set care plan history (all versions) - set this first
    const allCarePlans = data.carePlans?.all || [];
    // Sort in descending order (latest first) by version number or createdAt
    const sortedCarePlans = [...allCarePlans].sort((a, b) => {
      // First try to sort by version number
      const versionA = a.version || a.versionNumber || 0;
      const versionB = b.version || b.versionNumber || 0;
      if (versionA !== versionB) {
        return versionB - versionA; // Descending order
      }
      // If version numbers are same or not available, sort by createdAt
      const dateA = new Date(a.createdAt || a.createdDate || 0);
      const dateB = new Date(b.createdAt || b.createdDate || 0);
      return dateB - dateA; // Descending order (newest first)
    });
    setCarePlanHistory(sortedCarePlans);

    // Determine which version to select
    let versionToSelect = null;
    
    // If a specific version was requested (e.g., after update), find it first
    if (versionToSelectAfterFetch) {
      const targetVersionNum = versionToSelectAfterFetch.version || versionToSelectAfterFetch.versionNumber;
      const targetId = versionToSelectAfterFetch._id || versionToSelectAfterFetch.id;
      
      if (targetVersionNum) {
        // Find by version number
        versionToSelect = sortedCarePlans.find(
          (cp) => (cp.version || cp.versionNumber) === targetVersionNum
        );
      } else if (targetId) {
        // Find by ID
        versionToSelect = sortedCarePlans.find(
          (cp) => (cp._id || cp.id) === targetId
        );
      }
    }
    
    // If no specific version requested or not found, use default logic
    if (!versionToSelect) {
      // Find the latest active version (isActive === true) from sorted array
      versionToSelect = sortedCarePlans.find((cp) => cp.isActive === true);
      
      // If no active version found, use the first item in sorted array (which is the latest)
      if (!versionToSelect && sortedCarePlans.length > 0) {
        versionToSelect = sortedCarePlans[0];
      }
      
      // If still no version found and we have activeCarePlan from response, use it
      const activeCarePlan = data.carePlans?.active;
      if (!versionToSelect && activeCarePlan) {
        versionToSelect = activeCarePlan;
      }
    }
    
    if (versionToSelect) {
      setCarePlanData(versionToSelect);
      setSelectedVersion(versionToSelect);
      
      // Extract date-only value to prevent timezone shifts
      // Use extractDateOnly to handle any format the backend might return
      const normalizedReviewDate = extractDateOnly(versionToSelect.reviewDate);
      
      setSessionFormData({
        clientGoal: versionToSelect.clientGoal || "",
        objectives: versionToSelect.objectives || "",
        interventions: versionToSelect.interventions || "",
        sessionFrequency: versionToSelect.frequency || versionToSelect.sessionFrequency || "",
        otherSessionFrequencyText: versionToSelect.otherSessionFrequencyText || "",
        reviewDate: normalizedReviewDate,
      });
      setOriginalCarePlanData({
        clientGoal: versionToSelect.clientGoal || "",
        objectives: versionToSelect.objectives || "",
        interventions: versionToSelect.interventions || "",
        sessionFrequency: versionToSelect.frequency || versionToSelect.sessionFrequency || "",
        otherSessionFrequencyText: versionToSelect.otherSessionFrequencyText || "",
        reviewDate: normalizedReviewDate,
      });
    }
  }, []);

  // Fetch care plan data and history
  const fetchCarePlanData = useCallback(async (versionToSelectAfterFetch = null) => {
    if (!userId) return;

    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/get-diagnosis-and-care-plans/${userId}`,
        getConfigWithToken(user?.user?.token)
      );
      if (response.data.success && response.data.data) {
        processCarePlanData(response.data.data, versionToSelectAfterFetch);
      }
    } catch (error) {
      console.error("Error fetching care plan data:", error);
      toast.error("Failed to fetch care plan data");
    } finally {
      setLoading(false);
    }
  }, [userId, user?.user?.token, processCarePlanData]);

  // Handle version selection
  const handleVersionSelect = (version) => {
    // Force state update by creating a new object reference
    setSelectedVersion({ ...version });
    
    // Extract date-only value to prevent timezone shifts
    const normalizedReviewDate = extractDateOnly(version.reviewDate);
    
    setSessionFormData({
      clientGoal: version.clientGoal || "",
      objectives: version.objectives || "",
      interventions: version.interventions || "",
      sessionFrequency: version.frequency || version.sessionFrequency || "",
      otherSessionFrequencyText: version.otherSessionFrequencyText || "",
      reviewDate: normalizedReviewDate,
    });
    setCarePlanEditMode(null); // Exit edit mode when selecting version
  };

  // Handle Update button click
  const handleUpdateClick = () => {
    // Ensure selectedVersion is set to the currently displayed version
    if (!selectedVersion) {
      console.error("No version selected for update");
      toast.error("Please select a version to update");
      return;
    }
    
    setCarePlanEditMode("update");
    setOriginalCarePlanData({
      clientGoal: sessionFormData.clientGoal || "",
      objectives: sessionFormData.objectives || "",
      interventions: sessionFormData.interventions || "",
      sessionFrequency: sessionFormData.sessionFrequency || "",
      otherSessionFrequencyText: sessionFormData.otherSessionFrequencyText || "",
      reviewDate: sessionFormData.reviewDate || "",
    });
  };

  // Handle Create New button click
  const handleCreateNewClick = () => {
    // Store current data before clearing
    if (!originalCarePlanData) {
      setOriginalCarePlanData({
        clientGoal: sessionFormData.clientGoal || "",
        objectives: sessionFormData.objectives || "",
        interventions: sessionFormData.interventions || "",
        sessionFrequency: sessionFormData.sessionFrequency || "",
        otherSessionFrequencyText: sessionFormData.otherSessionFrequencyText || "",
        reviewDate: sessionFormData.reviewDate || "",
      });
    }
    setCarePlanEditMode("create");
    // Calculate review date as +180 days from now
    const today = new Date();
    const reviewDate = new Date(today);
    reviewDate.setDate(today.getDate() + 180);
    const formattedReviewDate = reviewDate.toISOString().split("T")[0];
    // Clear care plan fields for new entry and set review date to +180 days
    setSessionFormData({
      clientGoal: "",
      objectives: "",
      interventions: "",
      sessionFrequency: "",
      otherSessionFrequencyText: "",
      reviewDate: formattedReviewDate,
    });
  };

  // Handle Cancel button click
  const handleCancelEdit = () => {
    if (carePlanEditMode === "update" && originalCarePlanData) {
      // Restore original data for update mode
      setSessionFormData({
        ...originalCarePlanData,
      });
    } else if (carePlanEditMode === "create") {
      // Restore original data for create mode (or clear if no original data)
      if (originalCarePlanData) {
        setSessionFormData({
          ...originalCarePlanData,
        });
      } else {
        // Clear care plan fields if no original data exists
        setSessionFormData({
          clientGoal: "",
          objectives: "",
          interventions: "",
          sessionFrequency: "",
          otherSessionFrequencyText: "",
          reviewDate: "",
        });
      }
    }
    setCarePlanEditMode(null);
  };

  // Handle Save button click
  const handleCarePlanSave = async () => {
    if (!userId) {
      toast.error("User ID is missing");
      return;
    }

    // Validate all required care plan fields before saving
    const errors = [];
    const errorSetters = {};

    // Client Goal validation
    if (!sessionFormData.clientGoal || sessionFormData.clientGoal.trim() === "") {
      errors.push("Client Goal is required");
      errorSetters.clientGoalError = "Client Goal is required";
    }

    // Objectives validation
    if (!sessionFormData.objectives || sessionFormData.objectives.trim() === "") {
      errors.push("Objectives is required");
      errorSetters.objectivesError = "Objectives is required";
    }

    // Interventions validation
    if (!sessionFormData.interventions || sessionFormData.interventions.trim() === "") {
      errors.push("Interventions is required");
      errorSetters.interventionsError = "Interventions is required";
    }

    // Session Frequency validation
    if (!sessionFormData.sessionFrequency || sessionFormData.sessionFrequency === "") {
      errors.push("Session Frequency is required");
      errorSetters.sessionFrequencyError = "Session Frequency is required";
    }

    // If frequency is "Other", validate otherSessionFrequencyText
    if (
      sessionFormData.sessionFrequency === "Other" &&
      (!sessionFormData.otherSessionFrequencyText ||
        sessionFormData.otherSessionFrequencyText.trim() === "")
    ) {
      errors.push("Please specify frequency");
      errorSetters.sessionFrequencyError = "Please specify frequency";
    }

    // Review Date validation
    if (!sessionFormData.reviewDate || sessionFormData.reviewDate === "") {
      errors.push("Review Date is required");
      errorSetters.reviewDateError = "Review Date is required";
    }

    // Set all error states
    if (errors.length > 0) {
      if (errorSetters.clientGoalError) setClientGoalError(errorSetters.clientGoalError);
      if (errorSetters.objectivesError) setObjectivesError(errorSetters.objectivesError);
      if (errorSetters.interventionsError) setInterventionsError(errorSetters.interventionsError);
      if (errorSetters.sessionFrequencyError)
        setSessionFrequencyError(errorSetters.sessionFrequencyError);
      if (errorSetters.reviewDateError) setReviewDateError(errorSetters.reviewDateError);

      // Show error toasts
      errors.forEach((error, index) => {
        setTimeout(() => {
          toast.error(error);
        }, index * 300);
      });

      return; // Prevent save if validation fails
    }

    try {
      setCarePlanSaving(true);
      
      // Get client timezone (same as AddAppointments.js)
      const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Build payload
      let payload;
      
      // Format review date to ensure it's in YYYY-MM-DD format before sending
      // HTML date inputs always provide YYYY-MM-DD format, so we use it directly
      // IMPORTANT: Never convert to Date object - keep as string to avoid timezone shifts
      let formattedReviewDate = sessionFormData.reviewDate || "";
      
      // Ensure it's in YYYY-MM-DD format (date inputs should already be in this format)
      // Only normalize if it's not already in the correct format
      if (formattedReviewDate && !/^\d{4}-\d{2}-\d{2}$/.test(formattedReviewDate)) {
        formattedReviewDate = formatDateForBackend(formattedReviewDate);
      }
      
      if (carePlanEditMode === "update" && selectedVersion) {
        // For update: send the complete version with updated form values
        // Ensure we use the actual version number from the selectedVersion object
        payload = {
          ...selectedVersion,
          clientGoal: sessionFormData.clientGoal || "",
          objectives: sessionFormData.objectives || "",
          interventions: sessionFormData.interventions || "",
          frequency: sessionFormData.sessionFrequency || "",
          otherSessionFrequencyText: sessionFormData.otherSessionFrequencyText || "",
          reviewDate: formattedReviewDate,
          action: "update",
          // Ensure version number is included from the selectedVersion
          version: selectedVersion.version || selectedVersion.versionNumber,
          timeZone: clientTimeZone,
        };
        
      } else {
        // For create: send only form fields
        payload = {
          clientGoal: sessionFormData.clientGoal || "",
          objectives: sessionFormData.objectives || "",
          interventions: sessionFormData.interventions || "",
          frequency: sessionFormData.sessionFrequency || "",
          otherSessionFrequencyText: sessionFormData.otherSessionFrequencyText || "",
          reviewDate: formattedReviewDate,
          action: "create",
          timeZone: clientTimeZone,
        };
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/users/create-or-update-care-plan/${userId}`,
        payload,
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success) {
        // Update original care plan data with current values after successful save
        const currentCarePlanData = {
          clientGoal: sessionFormData.clientGoal || "",
          objectives: sessionFormData.objectives || "",
          interventions: sessionFormData.interventions || "",
          sessionFrequency: sessionFormData.sessionFrequency || "",
          otherSessionFrequencyText: sessionFormData.otherSessionFrequencyText || "",
          reviewDate: sessionFormData.reviewDate || "",
        };
        setOriginalCarePlanData(currentCarePlanData);

        // Exit edit mode after successful save
        setCarePlanEditMode(null);

        // Clear all errors
        setClientGoalError("");
        setObjectivesError("");
        setInterventionsError("");
        setSessionFrequencyError("");
        setReviewDateError("");

        // Prefer immediate UI update from API response to avoid stale view
        const responseData = response?.data?.data || null;
        const versionToSelect = carePlanEditMode === "update" ? { ...payload } : null;

        if (responseData && responseData.carePlans) {
          processCarePlanData(responseData, versionToSelect);
        } else {
          // Fallback: refresh from API (kept to ensure data consistency)
          await fetchCarePlanData(versionToSelect);
        }

        // Also trigger parent's refresh if provided (keeps shared data in sync)
        if (onRefresh) {
          await onRefresh();
        }

        toast.success("💾 Care plan saved successfully.", {
          position: "bottom-right",
          autoClose: 3000,
        });
      } else {
        // Show all validation errors from backend
        if (response.data.errors && Array.isArray(response.data.errors)) {
          response.data.errors.forEach((error, index) => {
            setTimeout(() => {
              toast.error(error);
            }, index * 300);
          });
        } else {
          toast.error(response.data.message || "Failed to save care plan");
        }
      }
    } catch (error) {
      console.error("Error saving care plan:", error);

      // Show all validation errors from backend
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        error.response.data.errors.forEach((err, index) => {
          setTimeout(() => {
            toast.error(err);
          }, index * 300);
        });
      } else {
        toast.error(
          error.response?.data?.message || "Unable to save care plan. Please try again."
        );
      }
    } finally {
      setCarePlanSaving(false);
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(carePlanHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedHistory = carePlanHistory.slice(startIndex, endIndex);

  // Reset to page 1 when history changes
  useEffect(() => {
    setCurrentPage(1);
  }, [carePlanHistory.length]);

  // Process initial data if provided, otherwise fetch
  useEffect(() => {
    if (initialData) {
      // Process the initial data without making an API call
      processCarePlanData(initialData);
    } else if (userId) {
      // Only fetch if no initial data provided and userId exists
      fetchCarePlanData();
    }
  }, [initialData, userId, processCarePlanData, fetchCarePlanData]);

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <div className="mt-3 text-muted">Loading care plan...</div>
      </div>
    );
  }

  return (
    <div className="g-4" style={{ marginLeft: 0, marginRight: 0, display: "flex", flexDirection: "row", width: "100%" }}>
      <div style={{ paddingLeft: 0, paddingRight: "12px", width: "49%" }}>
        <CarePlanConcernsSection
          sessionFormData={sessionFormData}
          updateFormData={updateFormData}
          clientGoalError={clientGoalError}
          objectivesError={objectivesError}
          interventionsError={interventionsError}
          sessionFrequencyError={sessionFrequencyError}
          reviewDateError={reviewDateError}
          setClientGoalError={setClientGoalError}
          setObjectivesError={setObjectivesError}
          setInterventionsError={setInterventionsError}
          setSessionFrequencyError={setSessionFrequencyError}
          setReviewDateError={setReviewDateError}
          autoResizeTextarea={autoResizeTextarea}
          isFollowUp={true}
          carePlanEditMode={carePlanEditMode}
          onUpdateClick={handleUpdateClick}
          onCreateNewClick={handleCreateNewClick}
          onCancelEdit={handleCancelEdit}
          onSave={handleCarePlanSave}
          saving={carePlanSaving}
        />
      </div>

      <div style={{ paddingLeft: "12px", paddingRight: 0, width: "51%" }}>
        <Card
          style={{
            border: "none",
            borderRadius: "16px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}>
          <Card.Body className="p-4" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <h3
              style={{
                color: "#1f2d3d",
                fontFamily: "Poppins",
                fontSize: "24px",
                fontWeight: "700",
                margin: 0,
                marginBottom: "24px",
                textAlign: "left",
              }}>
              Care Plan History
            </h3>

            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              {carePlanHistory.length === 0 ? (
                <div className="text-center py-5" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div className="text-muted">No care plan history available</div>
                </div>
              ) : (
                <>
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "12px", overflow: "hidden" }}>
                    {paginatedHistory.map((version, index) => {
                      const globalIndex = startIndex + index;
                      // Use the actual version number from the care plan object, not calculated
                      const versionNumber = version.version || version.versionNumber || (carePlanHistory.length - globalIndex);

                      // Compare versions - use version number as primary, ID as fallback
                      const versionId = version._id || version.id;
                      const selectedId = selectedVersion?._id || selectedVersion?.id;
                      const versionNum = version.version || version.versionNumber;
                      const selectedVersionNum = selectedVersion?.version || selectedVersion?.versionNumber;
                      
                      // Primary comparison: version numbers (most reliable)
                      let isSelected = false;
                      if (selectedVersion && versionNum && selectedVersionNum) {
                        isSelected = versionNum === selectedVersionNum;
                      } else if (selectedVersion && versionId && selectedId) {
                        // Fallback: compare IDs if version numbers not available
                        isSelected = versionId === selectedId;
                      } else if (selectedVersion) {
                        // Last resort: compare object references
                        isSelected = version === selectedVersion;
                      }

                      const createdDate = version.createdAt
                        ? formatDate(version.createdAt)
                        : version.createdDate
                        ? formatDate(version.createdDate)
                        : "—";
                      
                      const updatedDate = version.lastUpdated
                        ? formatDate(version.lastUpdated)
                        : version.updatedAt
                        ? formatDate(version.updatedAt)
                        : version.updatedDate
                        ? formatDate(version.updatedDate)
                        : "—";
                      
                      // Format review date directly from string to avoid timezone shifts
                      // reviewDate is a date-only value (YYYY-MM-DD), not a datetime
                      const reviewDate = version.reviewDate 
                        ? (() => {
                            const dateStr = version.reviewDate;
                            // If it's in YYYY-MM-DD format, format it directly without Date conversion
                            if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                              const [year, month, day] = dateStr.split('-');
                              // Format as MM/DD/YYYY (US format)
                              return `${parseInt(month)}/${parseInt(day)}/${year}`;
                            }
                            // Fallback: use formatDate for other formats (though reviewDate should always be YYYY-MM-DD)
                            return formatDate(version.reviewDate);
                          })()
                        : "—";

                      return (
                        <div
                          key={version._id || version.id || globalIndex}
                          onClick={() => handleVersionSelect(version)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: isSelected ? "#1E3A8A" : "#FFFFFF",
                            color: isSelected ? "#FFFFFF" : "#1f2d3d",
                            boxShadow: isSelected
                              ? "0 4px 12px rgba(30,58,138,0.25)"
                              : "0 2px 8px rgba(0,0,0,0.08)",
                            transition: "all 0.2s ease-in-out",
                            borderRadius: "12px",
                            padding: "20px",
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "flex-start",
                            gap: "24px",
                            width: "100%",
                            boxSizing: "border-box",
                            minWidth: 0,
                            overflow: "hidden",
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = "#f8f9fa";
                              e.currentTarget.style.color = "#1f2d3d";
                              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                            } else {
                              e.currentTarget.style.backgroundColor = "#1E3A8A";
                              e.currentTarget.style.color = "#FFFFFF";
                              e.currentTarget.style.boxShadow = "0 4px 12px rgba(30,58,138,0.35)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (isSelected) {
                              e.currentTarget.style.backgroundColor = "#1E3A8A";
                              e.currentTarget.style.color = "#FFFFFF";
                              e.currentTarget.style.boxShadow = "0 4px 12px rgba(30,58,138,0.25)";
                            } else {
                              e.currentTarget.style.backgroundColor = "#FFFFFF";
                              e.currentTarget.style.color = "#1f2d3d";
                              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                            }
                          }}
                        >
                          {/* Version Number */}
                          <div style={{ 
                            display: "flex", 
                            flexDirection: "column", 
                            gap: "4px", 
                            textAlign: "left",
                            minWidth: "80px",
                            flexShrink: 0
                          }}>
                            <div style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              opacity: isSelected ? 1 : 0.8,
                              textAlign: "left"
                            }}>
                              Version
                            </div>
                            <div style={{
                              fontSize: "16px",
                              fontWeight: "400",
                              textAlign: "left"
                            }}>
                              {versionNumber}
                            </div>
                          </div>

                          {/* Created Date */}
                          <div style={{ 
                            display: "flex", 
                            flexDirection: "column", 
                            gap: "4px", 
                            textAlign: "left",
                            flex: "1 1 0",
                            minWidth: 0
                          }}>
                            <div style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              opacity: isSelected ? 1 : 0.8,
                              textAlign: "left"
                            }}>
                              Created Date
                            </div>
                            <div style={{
                              fontSize: "16px",
                              fontWeight: "400",
                              textAlign: "left",
                              wordBreak: "break-word"
                            }}>
                              {createdDate}
                            </div>
                          </div>

                          {/* Updated Date */}
                          <div style={{ 
                            display: "flex", 
                            flexDirection: "column", 
                            gap: "4px", 
                            textAlign: "left",
                            flex: "1 1 0",
                            minWidth: 0
                          }}>
                            <div style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              opacity: isSelected ? 1 : 0.8,
                              textAlign: "left"
                            }}>
                              Updated Date
                            </div>
                            <div style={{
                              fontSize: "16px",
                              fontWeight: "400",
                              textAlign: "left",
                              wordBreak: "break-word"
                            }}>
                              {updatedDate}
                            </div>
                          </div>

                          {/* Review Date */}
                          <div style={{ 
                            display: "flex", 
                            flexDirection: "column", 
                            gap: "4px", 
                            textAlign: "left",
                            flex: "1 1 0",
                            minWidth: 0
                          }}>
                            <div style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              opacity: isSelected ? 1 : 0.8,
                              textAlign: "left"
                            }}>
                              Review Date
                            </div>
                            <div style={{
                              fontSize: "16px",
                              fontWeight: "400",
                              textAlign: "left",
                              wordBreak: "break-word"
                            }}>
                              {reviewDate}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", marginTop: "20px", paddingTop: "16px" }}>
                      <Button
                        variant="outline-secondary"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          fontWeight: "500",
                          padding: "6px 12px",
                        }}>
                        Previous
                      </Button>
                      <span style={{ color: "#1f2d3d", fontWeight: "500", padding: "0 12px" }}>
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline-secondary"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          fontWeight: "500",
                          padding: "6px 12px",
                        }}>
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default CarePlanCard;

